import { HtmlParser } from '@calendly/html-parser'
import { I18n } from '@calendly/i18n'
import { ContextualBanner } from '@calendly/ui/components/contextual-banner'
import { MegaphoneIcon } from '@calendly/ui/components/icons'
import { Link } from '@calendly/ui/components/link'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import BillingClickCapture from 'components/billing/click_capture'
import BillingInlineEnforcement from 'components/billing/inline_enforcement'
import { currentTierLowerSelector, isAvailableFeatureSelector } from 'features/billing'

const FEATURES_PER_KIND = {
  invitee_reminder: {
    activation: 'event_reminder_email',
    customization: 'event_type_notification'
  },
  invitee_sms_reminder: {
    activation: 'event_reminder_sms',
    customization: 'event_reminder_sms'
  },
  invitee_email_follow_up: {
    activation: 'event_follow_up_email',
    customization: 'event_type_notification'
  },
  invitee_invitation: {
    activation: 'event_type_notification',
    customization: 'event_type_notification'
  },
  invitee_confirmation: {
    activation: 'event_type_notification',
    customization: 'event_type_notification'
  },
  invitee_cancellation: {
    activation: 'event_cancellation_management',
    customization: 'event_cancellation_management'
  },
}

export const getActivationFeatureKey = (kind) => FEATURES_PER_KIND[kind]?.activation || null
const getCustomizationFeatureKey = (kind) => FEATURES_PER_KIND[kind]?.customization || null

const hasActivationEnforcement = (kind) => !['invitee_invitation', 'invitee_confirmation', 'invitee_cancellation'].includes(kind)

export const useShowActivationEnforcement = ({ isMETParent, kind }) => {
  const featureKey = getActivationFeatureKey(kind)
  const isAvailableFeature = useSelector((state) => isAvailableFeatureSelector(state, { featureKey }))
  const currentTier = useSelector(currentTierLowerSelector)

  if (!hasActivationEnforcement(kind)) {
    return false
  }

  return currentTier !== null && !isMETParent && !isAvailableFeature
}

export const useShowCustomizationEnforcement = ({ isMETParent, kind }) => {
  const featureKey = getCustomizationFeatureKey(kind)
  const isAvailableFeature = useSelector((state) => isAvailableFeatureSelector(state, { featureKey }))
  const currentTier = useSelector(currentTierLowerSelector)

  return currentTier !== null && !isMETParent && !isAvailableFeature
}

const PAYWALL_NAMES_PER_KIND = {
  invitee_reminder: {
    activation: 'event_reminder_email',
    customization: 'invitee_reminder'
  },
  invitee_email_follow_up: {
    activation: 'event_follow_up_email',
    customization: 'invitee_email_follow_up'
  },
  invitee_sms_reminder: {
    activation: 'event_reminder_sms',
    customization: 'event_reminder_sms'
  },
  invitee_invitation: {
    activation: 'invitee_invitation',
    customization: 'invitee_invitation'
  },
  invitee_confirmation: {
    activation: 'invitee_confirmation',
    customization: 'invitee_confirmation'
  },
  invitee_cancellation: {
    activation: 'event_cancellation_management',
    customization: 'event_cancellation_management'
  },
}

export const getActivationPaywallName = (kind) => PAYWALL_NAMES_PER_KIND[kind].activation || null

const getCustomizationPaywallName = (kind) => PAYWALL_NAMES_PER_KIND[kind].customization || null

// i18n-tasks-use t("publisher.event_type_editor.sections.communications.paywall.add.invitee_email_follow_up")
// i18n-tasks-use t("publisher.event_type_editor.sections.communications.paywall.add.invitee_reminder")
// i18n-tasks-use t("publisher.event_type_editor.sections.communications.paywall.add.invitee_sms_reminder")
// i18n-tasks-use t("publisher.event_type_editor.sections.communications.paywall.edit.invitee_cancellation")
// i18n-tasks-use t("publisher.event_type_editor.sections.communications.paywall.edit.invitee_confirmation")
// i18n-tasks-use t("publisher.event_type_editor.sections.communications.paywall.edit.invitee_email_follow_up")
// i18n-tasks-use t("publisher.event_type_editor.sections.communications.paywall.edit.invitee_invitation")
// i18n-tasks-use t("publisher.event_type_editor.sections.communications.paywall.edit.invitee_reminder")
export const UpgradeEnforcementBanner = ({ isMETParent, kind }) => {
  const showActivationEnforcement = useShowActivationEnforcement({ isMETParent, kind })
  const showCustomizationEnforcement = useShowCustomizationEnforcement({ isMETParent, kind })
  const activationFeatureKey = getActivationFeatureKey(kind)
  const activationPaywallName = getActivationPaywallName(kind)
  const ActivationBillingCaptureLink = useCallback(({ children } = {}) => (
    <BillingClickCapture
      featureName={activationFeatureKey}
      paywallOverride={activationPaywallName}
      skipPreface
      noTracking
    >
      <Link as="button" underline={false}>{children}</Link>
    </BillingClickCapture>
  ), [activationFeatureKey, activationPaywallName])


  const customizationFeatureKey = getCustomizationFeatureKey(kind)
  const customizationPaywallName = getCustomizationPaywallName(kind)

  const CustomizationBillingCaptureLink = useCallback(({ children } = {}) => (
    <BillingClickCapture
      featureName={customizationFeatureKey}
      paywallOverride={customizationPaywallName}
      skipPreface
      noTracking
    >
      <Link as="button" underline={false}>{children}</Link>
    </BillingClickCapture>
  ), [customizationFeatureKey, customizationPaywallName])


  if (!showActivationEnforcement && !showCustomizationEnforcement) {
    return null
  }


  if (showActivationEnforcement) {
    return (
      <ContextualBanner icon={MegaphoneIcon}>
        <BillingInlineEnforcement feature={activationFeatureKey}>
          <div>
            {
              HtmlParser(
                I18n.t(`publisher.event_type_editor.sections.communications.paywall.add.${kind}`),
                { LinkComponent: ActivationBillingCaptureLink }
              )
            }
          </div>
        </BillingInlineEnforcement>
      </ContextualBanner>
    )
  }

  return (
    <ContextualBanner icon={MegaphoneIcon}>
      <BillingInlineEnforcement feature={customizationFeatureKey}>
        <div>
          {
            HtmlParser(
              I18n.t(`publisher.event_type_editor.sections.communications.paywall.edit.${kind}`),
              { LinkComponent: CustomizationBillingCaptureLink }
            )
          }
        </div>
      </BillingInlineEnforcement>
    </ContextualBanner>
  )
}
